import {useMemo} from 'react';
import {AssetValidationError} from '@contentlab/api/project/types';
import useReportAssetErrors from '@contentlab/hooks/use-report-asset-errors';
import {AssetErrors, UploadedVideo} from '@contentlab/stores/contentLabStore';

/**
 * Parameters for the useMergedAssetErrors hook
 */
interface UseMergedAssetErrorsParams {
  /** Array of server-side asset validation errors */
  serverAssetErrors: AssetValidationError[];
  /** Array of client-side asset errors */
  clientAssetErrors: AssetErrors[];
  /** Array of uploaded video assets */
  uploadedVideos: UploadedVideo[];
}

/**
 * Return value from the useMergedAssetErrors hook
 */
interface UseMergedAssetErrorsReturn {
  /** Combined array of all asset errors (client + server) */
  assetErrors: AssetErrors[];
  /** Set of invalid asset IDs from server-side errors */
  invalidAssetIds: Set<string>;
  /** Set of invalid asset IDs from client-side errors */
  invalidClientAssetIds: Set<string>;
  /** Array of uploaded videos that don't have server-side errors */
  validUploadedVideos: UploadedVideo[];
  /** Array of uploaded videos that don't have client-side errors */
  validClientUploadedVideos: UploadedVideo[];
}

/**
 * Custom hook that merges client-side and server-side asset errors and filters uploaded videos
 *
 * This hook combines asset errors from both client and server sources into a single
 * array and processes uploaded videos to separate them into valid and invalid categories.
 * It creates Sets of invalid asset IDs for efficient lookup and filtered arrays of valid videos.
 *
 * @param params - The parameters object containing asset errors and uploaded videos
 * @param params.serverAssetErrors - Array of server-side asset validation errors
 * @param params.clientAssetErrors - Array of client-side asset errors
 * @param params.uploadedVideos - Array of uploaded video assets to validate
 *
 * @returns Object containing merged asset errors, invalid asset ID Sets and filtered valid video arrays
 *
 * @example
 * ```typescript
 * const { assetErrors, validUploadedVideos, invalidAssetIds } = useMergedAssetErrors({
 *   serverAssetErrors: serverValidationErrors,
 *   clientAssetErrors: clientValidationErrors,
 *   uploadedVideos: allVideos
 * });
 * ```
 */
function useMergedAssetErrors(
  params: UseMergedAssetErrorsParams
): UseMergedAssetErrorsReturn {
  const {clientAssetErrors, serverAssetErrors, uploadedVideos} = params;

  // Merge client and server asset errors into a single array
  const assetErrors = useMemo((): AssetErrors[] => {
    return clientAssetErrors.concat(serverAssetErrors as AssetErrors[]);
  }, [clientAssetErrors, serverAssetErrors]);

  useReportAssetErrors({assetErrors});

  // Create Sets for invalid asset IDs to enable O(1) filtering
  const {invalidAssetIds, invalidClientAssetIds} = useMemo(() => {
    return {
      // Convert server-side asset errors array to a Set for efficient filtering
      invalidAssetIds: new Set(
        assetErrors.map((assetError) => assetError.assetId)
      ),
      // Convert client-side asset errors array to a Set for efficient filtering
      invalidClientAssetIds: new Set(
        clientAssetErrors.map((assetError) => assetError.assetId)
      ),
    };
  }, [assetErrors, clientAssetErrors]);

  // Filter uploaded videos to exclude those with errors
  const {validUploadedVideos, validClientUploadedVideos} = useMemo(() => {
    return {
      // Filter out videos that have server-side errors
      validUploadedVideos: uploadedVideos.filter(
        (uploadedVideo) => !invalidAssetIds.has(uploadedVideo.id)
      ),
      // Filter out videos that have client-side errors
      validClientUploadedVideos: uploadedVideos.filter(
        (uploadedVideo) => !invalidClientAssetIds.has(uploadedVideo.id)
      ),
    };
  }, [uploadedVideos, invalidAssetIds, invalidClientAssetIds]);

  return {
    assetErrors,
    invalidAssetIds,
    invalidClientAssetIds,
    validUploadedVideos,
    validClientUploadedVideos,
  };
}

export default useMergedAssetErrors;
